import { useMutation, useQuery } from '@tanstack/react-query';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useCheckoutStore } from '@jwp/ott-common/src/stores/CheckoutStore';
import CheckoutController from '@jwp/ott-common/src/controllers/CheckoutController';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import type { Offer } from '@jwp/ott-common/types/checkout';
import { useMemo } from 'react';

const useOffers = () => {
  const checkoutController = getModule(CheckoutController);
  const accountController = getModule(AccountController);

  const { offers, switchSubscriptionOffers, requestedMediaProducts } = useCheckoutStore(
    ({ offers, switchSubscriptionOffers, requestedMediaProducts }) => ({ offers, switchSubscriptionOffers, requestedMediaProducts }),
    shallow,
  );

  const { isLoading: isInitialisationLoading } = useQuery<Offer[]>({
    queryKey: ['initialiseOffers', requestedMediaProducts],
    queryFn: checkoutController.initialiseOffers,
  });

  const chooseOffer = useMutation({
    mutationKey: ['chooseOffer'],
    mutationFn: checkoutController.chooseOffer,
  });

  const switchSubscription = useMutation({
    mutationKey: ['switchSubscription'],
    mutationFn: checkoutController.switchSubscription,
    onSuccess: () => accountController.reloadSubscriptions({ delay: 3000, retry: 10 }), // A subscription switch usually takes at least 3 secs
  });

  const filteredOffers = useMemo(() => {
    // If a premier offer is present, only show these, as a subscription is not sufficient
    if (requestedMediaProducts.some((mediaProduct) => mediaProduct.premier)) {
      // @todo: offerId is not (always) the same as product id
      return offers.filter(({ offerId }) => requestedMediaProducts.some(({ premier, id }) => premier && id === offerId));
    }

    return offers;
  }, [offers, requestedMediaProducts]);

  return {
    isLoading: isInitialisationLoading || chooseOffer.isPending,
    offers: filteredOffers,
    switchSubscriptionOffers,
    chooseOffer,
    switchSubscription,
  };
};

export default useOffers;
